// ADDED
import React, { useState, useMemo, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Input, TextArea } from "../../../../../common/Input";
import PayrollPeriodPickerField from "../../../../../common/selectComponents/PayrollPeriodPicker";
import { IDENTIFIER, LOAN_TYPES } from "../../../../../utils/enums";
import { predictSalaryAdvanceEndDate, formatDateLong } from "../../../../../utils/textUtils";
import SectionBadge from "../../EmployeeTransactions/ui/SectionBadge";
import EmployeeCard from "../../../ui/EmployeeCard";
import { useLoanTransaction } from "../../../../hooks/useLoanTransaction";

const LoanRequestModal = ({ isOpen, onClose, mode, employee, onSave, loading, user, showGeneralMessage }) => {
  const { fetchLoanDetails, fetchCurrentLoan } = useLoanTransaction(showGeneralMessage);

  // --- FORM STATES ---
  const [loanType, setLoanType] = useState("");
  const [totalLoan, setTotalLoan] = useState("");
  const [totalBalance, setTotalBalance] = useState("");
  const [defaultDeduction, setDefaultDeduction] = useState("");
  const [deductionFrequency, setDeductionFrequency] = useState("");
  const [startCutoffDate, setStartCutoffDate] = useState("");
  const [startCutoffPayrollPeriodId, setStartCutoffPayrollPeriodId] = useState("");
  const [startCutoffPayrollPeriodName, setStartCutoffPayrollPeriodName] = useState("");
  const [endDeductionCutoff, setEndDeductionCutoff] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [remarks, setRemarks] = useState("");

  // --- UI/VALIDATION STATES ---
  const [isBlocked, setIsBlocked] = useState(true); // Locked until valid type selected
  const [existingRecord, setExistingRecord] = useState(null);
  const [lastGeneratedParams, setLastGeneratedParams] = useState(null);
  const [errorState, setErrorState] = useState(false);

  const showIsActiveField = (mode !== "Recalculate" ? true : false)
  const showRemarksField = (mode === "Edit" && !isActive ? true : false)

  /**
   * Resets form fields to avoid state bleeding between types/modes
   */
  const resetFields = useCallback(() => {
    setTotalLoan("");
    setTotalBalance("");
    setDefaultDeduction("");
    setDeductionFrequency("");
    setStartCutoffDate("");
    setStartCutoffPayrollPeriodId("");
    setStartCutoffPayrollPeriodName("");
    setEndDeductionCutoff("");
    setIsActive(true);
    setRemarks("");
    setExistingRecord(null);
    setIsBlocked(true);
    setLastGeneratedParams(null);
    setErrorState(false);
  }, []);

  /**
   * Triggers whenever Loan Type or Mode changes.
   * Handles the logic for blocking/enabling fields.
   */
  useEffect(() => {
    const validateAndPopulate = async () => {
      if (!loanType || !isOpen || !employee?.id) {
        setIsBlocked(true);
        return;
      }

      const formattedLoanType = loanType
        .replaceAll('_', ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase())

      // 1. Fetch data for validation (Request record) and population (Main Loan record)
      const requestData = await fetchLoanDetails(loanType, employee.id);

      // 2. LOGIC FOR ADD MODE
      if (mode === "Add") {
        if (requestData) {
          setIsBlocked(true);
          showGeneralMessage(`${formattedLoanType} is already active. Use Overwrite instead.`, false);
        } else {
          setIsBlocked(false); // Enable for fresh entry
        }
      }

      // 3. LOGIC FOR EDIT / RECALCULATE
      else if (mode === "Edit" || mode === "Recalculate") {
        const loanData = await fetchCurrentLoan(loanType, employee.id);

        if (!loanData || !loanData.id) {
          setIsBlocked(true);
          showGeneralMessage(`No existing ${formattedLoanType} found for this employee to ${mode.toLowerCase()}.`, false);
        } else {
          setIsBlocked(false); // Unlock
          setExistingRecord(loanData);

          // Auto-populate from DB
          // 1. Prepare sanitized values
          const sanitizedValues = {
            totalLoan: loanData.totalLoan?.toString() || "0",
            totalBalance: loanData.totalBalance?.toString() || "0",
            defaultDeduction: loanData.defaultDeduction?.toString() || "0",
            deductionFrequency: loanData.deductionFrequency || "",
            startCutoffDate: loanData.startCutoffDate || "",
            startCutoffPayrollPeriodId: loanData.startCutoffPayrollPeriodId || "",
            startCutoffPayrollPeriodName: loanData.startCutoffPayrollPeriodName || "",
            endDeductionCutoff: loanData.endDeductionCutoff || "",
            isActive: loanData.isActive ?? true,
            remarks: loanData.remarks || ""
          };

          // 2. Set individual form states
          setTotalBalance(sanitizedValues.totalBalance);
          setTotalLoan(sanitizedValues.totalLoan);
          setDefaultDeduction(sanitizedValues.defaultDeduction);
          setDeductionFrequency(sanitizedValues.deductionFrequency);
          setStartCutoffDate(sanitizedValues.startCutoffDate);
          setStartCutoffPayrollPeriodId(sanitizedValues.startCutoffPayrollPeriodId);
          setStartCutoffPayrollPeriodName(sanitizedValues.startCutoffPayrollPeriodName);
          setEndDeductionCutoff(sanitizedValues.endDeductionCutoff);
          setIsActive(sanitizedValues.isActive);
          setRemarks(sanitizedValues.remarks);

          // 3. Set baseline (Sync point) using exact same sanitized values
          setLastGeneratedParams({
            totalBalance: sanitizedValues.totalBalance,
            totalLoan: sanitizedValues.totalLoan,
            defaultDeduction: sanitizedValues.defaultDeduction,
            startCutoffDate: sanitizedValues.startCutoffDate,
            deductionFrequency: sanitizedValues.deductionFrequency
          });
        }
      }
    };

    validateAndPopulate();
  }, [loanType, employee?.id, isOpen, mode, fetchLoanDetails, fetchCurrentLoan, showGeneralMessage]);

  const handleClose = () => {
    resetFields();
    setLoanType("");
    onClose();
  };

  const isBalanceOverLimit = parseFloat(totalBalance || 0) > parseFloat(totalLoan || 0);

  // Check if schedule needs re-generation
  const isDirty = useMemo(() => {
    // Always dirty in Add mode until first generate
    if (mode === "Add" && !lastGeneratedParams) return true;

    // If data is still loading (Edit/Recalculate), lastGeneratedParams might be null
    if (!lastGeneratedParams) return false;

    return (
      String(totalBalance) !== String(lastGeneratedParams.totalBalance) ||
      String(totalLoan) !== String(lastGeneratedParams.totalLoan) ||
      String(defaultDeduction) !== String(lastGeneratedParams.defaultDeduction) ||
      String(startCutoffDate) !== String(lastGeneratedParams.startCutoffDate) ||
      String(deductionFrequency) !== String(lastGeneratedParams.deductionFrequency)
    );
  }, [totalBalance, totalLoan, defaultDeduction, startCutoffDate, deductionFrequency, lastGeneratedParams, mode]);

  const handleGenerateClick = () => {
     if (isBalanceOverLimit) return; 

    if (!totalBalance || !totalLoan || !defaultDeduction || !startCutoffDate || !deductionFrequency) {
      setErrorState(true);
      return;
    }
    const endDate = predictSalaryAdvanceEndDate(totalBalance, defaultDeduction, startCutoffDate, deductionFrequency);
    setEndDeductionCutoff(endDate);
    setLastGeneratedParams({ totalBalance, totalLoan, defaultDeduction, startCutoffDate, deductionFrequency });
    setErrorState(false);
  };

  const handleFormSubmit = () => {
    if (isDirty || !loanType || isBlocked || isBalanceOverLimit) {
      setErrorState(true);
      if (isBalanceOverLimit) showGeneralMessage("Validation Error: Total Balance exceeds Total Loan.", false);
      if (isBlocked) showGeneralMessage("Operation blocked. Please select a valid loan type.", false);
      return;
    }

    const requestId = existingRecord?.sssLoanRequestId
      || existingRecord?.sssEmergencyLoanRequestId
      || existingRecord?.sssCalamityLoanRequestId
      || existingRecord?.pagibigLoanRequestId
      || existingRecord?.pagibigCalamityLoanRequestId
      || null;

    const payload = {
      id: existingRecord?.id || 0, // SSSLoans.Id / SSSEmergenLoans.Id etc..
      loanRequestId: requestId,
      employeePersonalInformationId: employee?.id,
      totalLoan: parseFloat(totalLoan || 0),
      totalBalance: parseFloat(totalBalance || 0),
      defaultDeduction: parseFloat(defaultDeduction || 0),
      deductionFrequency,
      startCutoffPayrollPeriodId,
      endDeductionCutoff,
      remarks,
      isActive,
      updatedBy: user?.id,
      // Legacy context for recalculation
      oldTotalLoan: existingRecord?.sssLoanRequestTotalLoan,
      oldDateGranted: existingRecord?.sssLoanRequestDateGranted
    };
    console.log({ payload })
    onSave(loanType, mode, payload, handleClose);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700">
      <div className="bg-white w-full md:w-[60%] lg:w-[45%] rounded-lg shadow-2xl flex flex-col max-h-[95vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 uppercase tracking-tight">
            {mode === "Recalculate" ? "Overwrite" : mode} Loan Request
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"><CloseIcon /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Section 1: Employee & Type Selection */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <SectionBadge label="Target Employee" editable={true} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Employee ID" value={employee?.employeeId ?? ""} isViewOnly={true} className="bg-white" />
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 mb-2 uppercase">Loan Type:</label>
                <select
                  value={loanType ?? ""}
                  onChange={(e) => {
                    setLoanType(e.target.value);
                    resetFields(); // Logic triggered by useEffect
                  }}
                  className="border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">-- Select Loan Type --</option>
                  {LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {errorState && !loanType && <span className="text-[10px] text-red-500 mt-1">Selection required to unlock form</span>}
              </div>
            </div>
            <EmployeeCard data={employee ? { fullName: employee.fullName, employeeId: employee.employeeId, position: employee.positionDescription } : null} />
          </div>

          {/* Section 2: Loan Configuration (Blocked until Validated) */}
          <div className={`border border-gray-200 p-5 rounded-xl bg-white shadow-sm space-y-5 transition-all duration-300 ${isBlocked ? 'opacity-40 grayscale pointer-events-none' : 'opacity-100'}`}>
            <SectionBadge label="Loan Configuration" editable={!isBlocked} />

            <div className="grid grid-cols-2 gap-5">
              <Input
                label="Total Balance"
                type="number"
                placeholder={(mode !== "Edit") ? "Enter Loan Balance..." : "--"}
                value={totalBalance ?? ""}
                onChange={e => setTotalBalance(e.target.value)}
                isDisabled={mode === "Edit"} // Only editable in Edit mode
                error={isBalanceOverLimit}
                errorMessage="Balance cannot be higher than Total Loan"
              />
              <Input
                label="Total Loan"
                type="number"
                value={totalLoan ?? ""}
                placeholder={(mode !== "Edit") ? "Enter Loan Amount..." : "--"}
                onChange={e => setTotalLoan(e.target.value)}
                isDisabled={mode === "Edit"} // Standard logic: Cannot change original loan in Edit mode
              />
              <Input
                label="Total Deduction"
                type="number"
                placeholder="Enter Deduction"
                value={defaultDeduction ?? ""}
                onChange={e => setDefaultDeduction(e.target.value)}
              />

              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 mb-1 uppercase">Deduction Timing:</label>
                <select
                  value={deductionFrequency ?? ""}
                  onChange={e => {
                    setDeductionFrequency(e.target.value);
                    setStartCutoffPayrollPeriodId(""); // Clear linked fields on frequency change
                    setStartCutoffPayrollPeriodName("");
                  }}
                  className="border border-gray-300 rounded-md p-2 text-sm bg-white"
                >
                  <option value="">-- Select --</option>
                  <option value={IDENTIFIER.EVERYCUTOFF}>Every Cutoff</option>
                  <option value={IDENTIFIER.FIRST}>First Cutoff</option>
                  <option value={IDENTIFIER.SECOND}>Second Cutoff</option>
                </select>
              </div>

              <PayrollPeriodPickerField
                label="Start Period"
                payrollPeriodId={startCutoffPayrollPeriodId ?? ""}
                payrollPeriodIdentifier={deductionFrequency}
                displayValue={startCutoffPayrollPeriodName || "-- Select --"}
                onSelect={(p) => {
                  setStartCutoffPayrollPeriodId(p.id);
                  setStartCutoffPayrollPeriodName(p.name);
                  setStartCutoffDate(p.dateFrom);
                }}
                onClear={() => {
                  setStartCutoffPayrollPeriodId("");
                  setStartCutoffPayrollPeriodName("");
                  setStartCutoffDate("");
                }}
                disabled={!deductionFrequency}
              />

              <Input
                label="End Period (Projected)"
                value={endDeductionCutoff ? formatDateLong(endDeductionCutoff) : "--"}
                isViewOnly={true}
                className="bg-blue-50 text-blue-900 font-bold"
              />
            </div>

            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleGenerateClick}
                className={`w-full py-2 rounded font-bold text-xs uppercase tracking-wider border-none cursor-pointer transition-all ${isDirty ? 'bg-orange-500 text-white shadow-md hover:bg-orange-600' : 'bg-green-100 text-green-700 border border-green-200'}`}
              >
                {isDirty ? "Generate Schedule" : "✓ Schedule Synced"}
              </button>
            </div>

            {showRemarksField && (
              <TextArea
                label="Remarks"
                value={remarks ?? ""}
                onChange={e => setRemarks(e.target.value)}
                placeholder="Add internal notes or reasons for modification..."
                rows={2}
              />
            )}
            {showIsActiveField && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="loanActive"
                  checked={isActive ?? true}
                  onChange={e => setIsActive(e.target.checked)}
                  className="w-4 h-4 accent-blue-600 cursor-pointer"
                />
                <label htmlFor="loanActive" className="text-sm font-bold text-gray-600 cursor-pointer select-none">
                  IS ACTIVE?
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-lg gap-2">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-gray-400 text-white rounded font-bold border-none cursor-pointer hover:bg-gray-500 transition-colors"
          >
            CANCEL
          </button>
          <button
            disabled={isDirty || loading || isBlocked || isBalanceOverLimit}
            onClick={handleFormSubmit}
            className={`px-10 py-2 rounded font-bold text-white border-none cursor-pointer transition-all ${isDirty || isBlocked ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {loading ? "SAVING..." : `SAVE ${mode.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoanRequestModal;